import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  Breadcrumbs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Button,
  Box,
} from "@mui/material";
import Link from "@mui/material/Link";
import axios from "axios";
import Layout from "../../../layout/Layout";
import { AlertMessage } from "../../../components/AlertMessage";
import {
  config,
  companyModulesAPI,
  manageCompaniesAPI,
  userData,
} from "../../../config";

// Company-level Home module access. A super-admin picks a company and toggles
// which modules that company's staff see on the mobile Home screen. Default
// (no saved row) = enabled, so a company shows everything until it is
// explicitly restricted here. Mirrors the backend MODULE_CATALOG
// (kg-backend/models/companyModulesModel.js) -- labels come from the server.
function CompanyModuleAccess() {
  const childRef = useRef();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [modules, setModules] = useState([]); // [{module_key, label, enabled}]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = () => {
    axios({
      method: "POST",
      url: manageCompaniesAPI,
      headers: config.headers,
      data: JSON.stringify({ intType: 5 }),
    })
      .then((response) => {
        setCompanies(response.data.result || []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setModules([]);
    if (!companyId) return;

    axios({
      method: "GET",
      url: `${companyModulesAPI}/by-company/${companyId}`,
      headers: config.headers,
    })
      .then((response) => {
        setModules(response.data.result || []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleToggle = (moduleKey, val) => {
    setModules((prev) =>
      prev.map((m) =>
        m.module_key === moduleKey ? { ...m, enabled: val ? 1 : 0 } : m
      )
    );
  };

  const handleSetAll = (val) => {
    setModules((prev) => prev.map((m) => ({ ...m, enabled: val ? 1 : 0 })));
  };

  const handleSave = () => {
    if (!selectedCompany) {
      childRef.current.triggerAlert(
        JSON.stringify({
          message: "Please select a company",
          messageType: "error",
        })
      );
      return;
    }

    setLoading(true);
    axios({
      method: "POST",
      url: `${companyModulesAPI}/save`,
      headers: config.headers,
      data: JSON.stringify({
        intCompanyId: selectedCompany,
        intActorID: userData?.emp_id,
        modules: modules.map((m) => ({
          module_key: m.module_key,
          enabled: m.enabled ? 1 : 0,
        })),
      }),
    })
      .then((response) => {
        setLoading(false);
        setModules(response.data.result || modules);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: response.data.message || "Module access saved",
            messageType: "success",
          })
        );
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
        childRef.current.triggerAlert(
          JSON.stringify({
            message: "Something went wrong while saving",
            messageType: "error",
          })
        );
      });
  };

  const enabledCount = modules.filter((m) => m.enabled).length;
  const allEnabled = modules.length > 0 && enabledCount === modules.length;

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <AlertMessage ref={childRef} />
        <Grid container spacing={3}>
          <Grid textAlign="center" item className="form-lable" xs={12} lg={6} md={6}>
            <Typography className="page_title" textAlign="left">
              Company Module Access
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Settings</Typography>
              <Typography color="text.primary">Company Module Access</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <div className="empty-space-20"></div>

        <Grid container spacing={2} margin="15px 0px" padding="10px 15px 0px 0px" alignItems="center">
          <Grid item xs={12} lg={4} md={4}>
            <FormControl fullWidth>
              <InputLabel id="company-select-label">Select Company</InputLabel>
              <Select
                labelId="company-select-label"
                label="Select Company"
                value={selectedCompany}
                onChange={(e) => handleCompanyChange(e.target.value)}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={8} md={8} textAlign="right">
            {selectedCompany ? (
              <>
                <Button
                  variant="outlined"
                  onClick={() => handleSetAll(!allEnabled)}
                  sx={{ borderColor: "#0c54a0", color: "#0c54a0", marginRight: "12px" }}
                >
                  {allEnabled ? "Disable All" : "Enable All"}
                </Button>
                <Button
                  className="btn-primary"
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                >
                  Save Access
                </Button>
              </>
            ) : null}
          </Grid>
        </Grid>

        <div className="empty-space-20"></div>

        {selectedCompany ? (
          <TableContainer component={Paper} elevation={3} sx={{ borderRadius: "10px" }}>
            <Table aria-label="company module access table">
              <TableHead sx={{ backgroundColor: "#0c54a0" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px" }}>
                    Home Module
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#fff", fontWeight: "bold", fontSize: "15px", width: "180px" }}>
                    Show on Home ({enabledCount}/{modules.length})
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((m) => (
                  <TableRow key={m.module_key} hover>
                    <TableCell sx={{ fontWeight: "500", color: "#333" }}>
                      {m.label}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={!!m.enabled}
                        onChange={(e) => handleToggle(m.module_key, e.target.checked)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box textAlign="center" py={4} sx={{ border: "1px dashed #ccc", borderRadius: "10px" }}>
            <Typography color="text.secondary">
              Select a company above to manage which modules appear on its staff's mobile Home screen.
            </Typography>
          </Box>
        )}
      </Grid>
    </Layout>
  );
}

export default CompanyModuleAccess;
